import { useRef, useEffect, useCallback, useState } from 'react';

interface UseTimeoutResult {
  stop: () => void;
  start: () => void;
  restart: () => void;
  isRunning: boolean;
}

const useTimeout = (callback: () => void, delay: number): UseTimeoutResult => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);
  const delayRef = useRef(delay);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    callbackRef.current = callback;
    delayRef.current = delay;
  });

  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      setIsRunning(false);
    }
  }, []);

  const start = useCallback(() => {
    if (!timeoutRef.current) {
      setIsRunning(true);
      timeoutRef.current = setTimeout(() => {
        callbackRef.current();
        stop();
      }, delayRef.current);
    }
  }, [stop]);

  const restart = useCallback(() => {
    stop();
    start();
  }, [stop, start]);

  useEffect(() => { return stop }, [stop]);

  return { stop, start, restart, isRunning };
};

export default useTimeout;